import { useEffect, useRef, useState } from 'react'
import { useSwipe } from '../lib/swipe'
import type { Category, Transaction } from '../db/database'
import type { PlannedTag } from '../db/budget'
import { Icon, type IconName } from './Icon'
import { formatSigned } from '../lib/money'
import { formatDay } from '../lib/dates'
import './TransactionRow.css'

/**
 * D'où vient cette dépense, quand elle vient d'un plan d'enveloppe.
 *
 * Neuf lignes « École −3 333 Ar » à la suite ne se distinguent par rien : c'est
 * le rang de la journée qui rend l'historique lisible. Une échéance unique se
 * nomme, elle aussi — sinon rien ne dit qu'elle a été prévue plutôt que saisie.
 */
function planLabel(plan: PlannedTag): string | null {
  if (plan.mode === 'once') return 'Payé en une fois'
  if (plan.mode !== 'daily') return null
  // « Journée 1 sur 1 » n'apprend rien : le rang n'a de sens qu'au milieu de
  // plusieurs journées identiques, ce qui est justement le problème qu'il règle.
  return plan.total > 1 ? `Journée ${plan.index} sur ${plan.total}` : 'Journée prévue'
}

export function TransactionRow(
  { transaction, category, plan, showDate = false, onOpen, onEdit, onDelete }: {
    transaction: Transaction
    category?: Category
    plan?: PlannedTag
    /** Ouvrir le détail de l'opération. Absent = la ligne reste inerte. */
    onOpen?: (transaction: Transaction) => void
    /**
     * Ouvrir directement la correction — le raccourci du glissement vers la
     * gauche et l'entrée « Modifier » du menu. Distinct de `onOpen` : toucher
     * la ligne mène à la LECTURE, corriger est un geste choisi.
     */
    onEdit?: (transaction: Transaction) => void
    /**
     * Demander la suppression. Absent = ni menu, ni glissement.
     *
     * Le libellé remonte avec l'opération : c'est ICI qu'il se calcule, en
     * arbitrant entre titre, catégorie et note. Le recalculer ailleurs, c'est
     * annoncer « Opération » là où l'écran affichait « Autre revenu ».
     */
    onDelete?: (transaction: Transaction, label: string) => void
    /**
     * Dater la ligne. Vrai sur l'accueil, où les dernières opérations se
     * suivent sans repère ; faux sur l'écran des opérations, qui les groupe
     * déjà par jour et répéterait la date sous chaque titre de journée.
     */
    showDate?: boolean
  },
) {
  const [menu, setMenu] = useState(false)
  const menuRef = useRef<HTMLSpanElement>(null)

  /*
   * Ce qui NOMME la ligne, du plus précis au plus général.
   *
   * Un titre saisi bat le nom de la catégorie : « écolage de Rina » situe la
   * dépense, « École » la range. La note ne sert de nom qu'en dernier recours,
   * pour les lignes écrites avant que le titre existe.
   */
  const label = transaction.title ?? category?.name ?? transaction.note ?? 'Sans catégorie'

  const actionnable = onEdit !== undefined && onDelete !== undefined

  /*
   * Le glissement, en pointer events (`lib/swipe.ts`).
   *
   * Vers la GAUCHE on corrige, vers la DROITE on retire : le sens du geste suit
   * celui de l'icône révélée derrière la ligne.
   */
  const swipe = useSwipe({
    enabled: actionnable,
    onLeft: () => onEdit?.(transaction),
    onRight: () => onDelete?.(transaction, label),
  })
  const { dx } = swipe

  /*
   * Le menu se referme au clic AILLEURS, comme tout menu contextuel.
   *
   * En phase de capture, pour qu'un clic sur une autre ligne ferme celui-ci
   * avant d'ouvrir le sien. Mais il faut alors épargner les clics DANS le menu :
   * sans ce test, fermer retirait les boutons du DOM avant que React ne
   * déclenche leur `onClick` — le menu se refermait sans rien faire. Vu dans le
   * navigateur ; jsdom, lui, laissait passer.
   */
  useEffect(() => {
    if (!menu) return
    const fermer = (event: MouseEvent) => {
      if (menuRef.current?.contains(event.target as Node)) return
      setMenu(false)
    }
    window.addEventListener('click', fermer, { capture: true })
    return () => window.removeEventListener('click', fermer, { capture: true })
  }, [menu])

  const income = transaction.direction === 'income'
  // Deux repères distincts, jamais fondus en une phrase : ce que l'utilisateur
  // a écrit (« riz, viande ») et ce que l'app sait du plan.
  // La date d'abord : c'est la première question qu'on se pose devant une liste
  // d'opérations. Le jour seul, jamais l'heure — `operation_date` n'en porte
  // pas, et l'horloge de saisie n'est pas celle de la dépense.
  const details = [
    showDate ? formatDay(transaction.operation_date) : null,
    // La catégorie n'apparaît ici que si un titre lui a pris la première ligne.
    transaction.title && category ? category.name : null,
    transaction.note && transaction.note !== label ? transaction.note : null,
  ].filter((part): part is string => part !== null)

  // L'origine se lit COMME UNE ÉTIQUETTE, contre le nom : mêlée à la date et à
  // la note, elle se noyait dans une ligne de texte gris qu'on ne lit plus.
  const tag = plan ? planLabel(plan) : null

  const inner = (
    <>
      <span className={`row-icon ${income ? 'is-income' : 'is-expense'}`}>
        <Icon name={(category?.icon as IconName) ?? 'other'} size={20} />
      </span>
      <span className="row-body">
        <span className="row-head">
          <span className="row-name">{label}</span>
          {tag && <span className="row-tag">{tag}</span>}
        </span>
        {details.length > 0 && (
          <span className="row-note">
            {/* Un élément par repère, et le séparateur en CSS : concaténés en
                une seule chaîne, ils deviennent un bloc de texte qu'on ne peut
                plus cibler — ni au test, ni au style. Le « · » est décoratif,
                sa place n'est pas dans le texte lu. */}
            {details.map((part, i) => <span key={i}>{part}</span>)}
          </span>
        )}
      </span>
      <span className={`row-amount tabular ${income ? 'is-income' : 'is-expense'}`}>
        {formatSigned(transaction.amount, transaction.direction)}
      </span>
    </>
  )

  // Un `<button>` et non un `onClick` sur le `<li>` : ce qui s'active au doigt
  // doit s'activer au clavier, et être annoncé comme actionnable.
  return (
    <li className="row-item">
      <span className="row-clip">
        {/* Ce qui attend derrière la ligne : l'icône dit ce que le geste va
            faire, AVANT de le faire. Un glissement sans annonce est une action
            qu'on découvre une fois qu'elle est faite. */}
        {actionnable && dx !== 0 && (
          <span className={`row-hint ${dx < 0 ? 'is-edit' : 'is-delete'}
                            ${swipe.armed ? 'is-armed' : ''}`}
                aria-hidden="true">
            <Icon name={dx < 0 ? 'pencil' : 'trash'} size={20} />
          </span>
        )}

        <span className="row-slide"
              style={dx ? { transform: `translateX(${dx}px)` } : undefined}>
        {onOpen ? (
          <button type="button" className="row is-openable"
                  {...swipe.handlers}
                  // Un glissement qui vient de se terminer ne doit pas ouvrir
                  // la feuille par-dessus le marché.
                  onClick={() => { if (!swipe.consume()) onOpen(transaction) }}>
            {inner}
          </button>
        ) : (
          <div className="row">{inner}</div>
        )}

        </span>
      </span>

      {/* HORS du conteneur qui rogne : le menu déborde volontairement de la
          ligne, et un `overflow: hidden` commun le décapitait. */}
      {actionnable && (
        <span className="row-menu" ref={menuRef}>
            <button type="button" className="row-menu-open"
                    aria-label={`Actions pour ${label}`}
                    aria-expanded={menu}
                    onClick={(e) => { e.stopPropagation(); setMenu((m) => !m) }}>
              <Icon name="more" size={18} />
            </button>

          {menu && (
            <span className="row-actions" role="menu">
              <button type="button" role="menuitem"
                    onClick={() => { setMenu(false); onEdit?.(transaction) }}>
                <Icon name="pencil" size={16} /> Modifier
              </button>
              <button type="button" role="menuitem" className="is-danger"
                      onClick={() => { setMenu(false); onDelete?.(transaction, label) }}>
                <Icon name="trash" size={16} /> Supprimer
              </button>
            </span>
          )}
        </span>
      )}
    </li>
  )
}
